import React, { useState } from 'react'
import { Box, Button, Chip, Tooltip, Typography, useTheme } from '@mui/material'
import { GridApiPremium, GridFooterContainer, GridRowSelectionModel } from '@mui/x-data-grid-premium'
import useUploadContactPhoto from '../../api/customHooks/useUploadContactPhoto'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { deleteButtonStyles } from './style'
import { Contact } from 'types'

const tooltipGridFooter = title => {
  return <Typography>{title}</Typography>
}

export const ContactsGridFooter = ({
  rowSelectionModel,
  onUpdate,
  anchorEl,
  setAnchorEl,
  onClickDelete,
  handleConfirmDelete,
  handleClosePopover,
  selectedData,
  setSelectedData,
  apiRef,
}: {
  rowSelectionModel: GridRowSelectionModel
  onUpdate: () => void
  anchorEl: HTMLElement | null
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  onClickDelete: (
    rowSelectionModel: GridRowSelectionModel,
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>,
    apiRef: React.RefObject<GridApiPremium>
  ) => void
  handleConfirmDelete: () => Promise<void>
  handleClosePopover: () => void
  selectedData: Contact
  setSelectedData: React.Dispatch<React.SetStateAction<Contact>>
  apiRef: React.RefObject<GridApiPremium>
}) => {
  const theme = useTheme()
  const { mutate: uploadMutation } = useUploadContactPhoto()
  const { showSnackbar } = useSnackbarContext()
  const [file, setFile] = useState(null)

  const handleFileChange = event => {
    const selectedFile = event.target.files[0]
    setFile(selectedFile)

    console.log('[handleFileChange] selectedFile:', selectedFile)
    console.log('[handleFileChange] selectedData:', selectedData)

    if (selectedFile && selectedData) {
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('contactId', selectedData.contacts_uuid)

      console.log('[handleFileChange] FormData:', formData.get('file'), formData.get('contactId'))

      uploadMutation(
        { formData },
        {
          onSuccess: data => {
            console.log('Photo uploaded successfully!', data)
            showSnackbar('Photo uploaded successfully!', 'success')

            setSelectedData(null)
          },
          onError: error => {
            console.error('Error uploading photo:', error)
            showSnackbar('Failed to upload photo. Please try again.', 'error') // Handle errors gracefully
          },
        }
      )
    }
  }

  const open = Boolean(anchorEl)
  const id = open ? 'contacts-grid-footer-popover' : undefined

  const selectionCount = Array.from(rowSelectionModel?.ids ?? []).length

  return (
    <GridFooterContainer
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 52,
        px: 2,
      }}
    >
      {/* Left: selection label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 1 }}>
        <Typography>Selected</Typography>
        <Chip label={selectionCount} sx={{ color: 'rgb(24, 144, 255)', fontSize: 18 }} />
        <Typography>{selectionCount === 1 ? 'Contact' : 'Contacts'}</Typography>
      </Box>

      {/* Right: action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 1 }}>
        {selectionCount === 1 && (
          <>
            <input type="file" onChange={handleFileChange} style={{ display: 'none' }} id="contact-photo-upload" />
            <label htmlFor="contact-photo-upload" style={{ cursor: 'pointer' }}>
              <Tooltip title={tooltipGridFooter('Upload Photo')} placement="top">
                <Button color="primary" variant="outlined" component="span">
                  Upload Photo
                </Button>
              </Tooltip>
            </label>
            <Tooltip
              title={
                selectedData?.dealCount > 0
                  ? `Cannot delete — connected to ${selectedData.dealCount} deal${selectedData.dealCount === 1 ? '' : 's'} as contact, primary contact, or signing recipient`
                  : tooltipGridFooter('Delete')
              }
              placement="top"
            >
              <span>
                <Button
                  sx={deleteButtonStyles(theme)}
                  variant="outlined"
                  onClick={() => onClickDelete(rowSelectionModel, setAnchorEl, apiRef)}
                  color="secondary"
                  disabled={selectedData?.dealCount > 0}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
          </>
        )}
        <Tooltip title="Under Development..." placement="top">
          <Box>
            <Button color="secondary" variant="contained" onClick={onUpdate} disabled sx={{ height: 40 }}>
              {selectionCount === 1 ? 'Update' : 'Mass Update'}
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </GridFooterContainer>
  )
}
